import { useFormik } from 'formik';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import { isIbanCountry } from '@/modules/common/utils/bank-details.ts';
import {
  IBAN_REGEX,
  SWIFT_BIC_REGEX,
  US_ROUTING_NUMBER_REGEX,
} from '@/modules/common/utils/regex.ts';
import { useRegisterShareholderList } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import { OnboardingHolderWrapper } from '@/modules/issuer-dashboard/components/holder-create/OnboardingHolderWrapper.tsx';
import { OnboardingStepProps } from '@/modules/issuer-dashboard/components/holder-create/roadsTypes.tsx';
import { dividendReinvestmentOptions } from '@/modules/issuer-dashboard/components/modals/IssuerAddSubscriptionModal.tsx';
import {
  useIssuerGetInvestorEditByIdQuery,
  useUpdateInvestorEditMutation,
} from '@/modules/issuer-dashboard/queries/holder-create.tsx';
import { Alert, InfoCard, SelectButtons, TextInputField } from 'ui';
import { useUnsavedChangesStore } from 'ui/hooks';
import { AlertIcon } from 'ui/icons';
import { UpdateEntityRequest } from 'api';

const validationSchema = yup.object({
  bank_name: yup.string().trim().required('This field is required'),
  country: yup.string().trim(),
  bank_account_bsb: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return country === 'AUS' ? schema.required('This field is required') : schema.optional();
    }),
  bank_account_swift: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return country !== 'AUS'
        ? schema
            .required('This field is required')
            .test(
              'is-valid-swift-bic',
              'Invalid SWIFT/BIC. Enter 8 or 11 characters. Example: DEUTDEFF',
              value => value.toLowerCase() === 'unknown' || SWIFT_BIC_REGEX.test(value),
            )
        : schema.optional();
    }),
  bank_account_iban: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return isIbanCountry(country)
        ? schema
            .required('This field is required')
            .test(
              'is-valid-iban',
              'Invalid IBAN. Enter 15 to 34 characters. Example: DE89370400440532013000',
              value => value.toLowerCase() === 'unknown' || IBAN_REGEX.test(value),
            )
        : schema.optional();
    }),
  bank_account_routing_number: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      return country === 'USA'
        ? schema
            .required('This field is required')
            .test(
              'is-valid-us-routing-number',
              'Invalid routing number. Enter 9 digits. Example: 021000021',
              value => value.toLowerCase() === 'unknown' || US_ROUTING_NUMBER_REGEX.test(value),
            )
        : schema.optional();
    }),
  bank_account_name: yup.string().trim().required('This field is required'),
  bank_account_number: yup.string().trim().required('This field is required'),
  dividend_reinvestment: yup.lazy((obj: any) =>
    yup.object(
      Object.fromEntries(
        Object.keys(obj ?? {}).map(key => [key, yup.boolean().required('This select is required')]),
      ),
    ),
  ),
});

const initialValues = {
  bank_name: '',
  country: '',
  bank_account_bsb: '',
  bank_account_swift: '',
  bank_account_iban: '',
  bank_account_routing_number: '',
  bank_account_name: '',
  bank_account_number: '',
  dividend_reinvestment: {},
};

export const PaymentInstructionsHolder = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: investor_edit_id = 0 } = useParams();
  const { state } = useLocation();
  const [params] = useSearchParams();
  const { data: dataHolder } = useIssuerGetInvestorEditByIdQuery(
    investor_edit_id ? +investor_edit_id : -1,
    !investor_edit_id || +investor_edit_id === 0 || isNaN(+investor_edit_id),
  );

  const isUpdate = useMemo(
    () =>
      state?.action === 'update' ||
      !!dataHolder?.data?.old_data?.shareholder_id ||
      !!dataHolder?.data?.new_data?.entity_data?.document_files?.length,
    [dataHolder?.data, state?.action],
  );

  const registerId = params.get(REGISTER_ID_SEARCH_PARAM_KEY);

  const { data: shareholderData } = useRegisterShareholderList(
    {
      entity_id:
        (dataHolder?.data?.old_data?.entity_id && +dataHolder?.data?.old_data?.entity_id) || 0,
      register_id: (registerId && +registerId) || 0,
    },
    { disabled: !dataHolder?.data?.old_data?.entity_id || !isUpdate },
  );

  const getInitialValues = useCallback(() => {
    return {
      ...initialValues,
      ...dataHolder?.data?.new_data?.entity_data,
      dividend_reinvestment: dataHolder?.data?.new_data?.dividend_reinvestment,
    };
  }, [dataHolder?.data?.new_data]);

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: getInitialValues(),
    onSubmit: async ({ dividend_reinvestment, ...values }) => {
      setDirty(false);
      if (formik.dirty) {
        const paymentDetails = {
          ...values,
          bank_account_bsb:
            values.country === 'AUS'
              ? values.bank_account_bsb
              : values.bank_account_bsb || 'Unknown',
          bank_account_swift: values.country === 'AUS' ? '' : values.bank_account_swift,
          bank_account_iban: isIbanCountry(values.country) ? values.bank_account_iban : '',
          bank_account_routing_number:
            values.country === 'USA' ? values.bank_account_routing_number : '',
        };

        await updateEntity({
          id: dataHolder?.data?.id,
          user_data: dataHolder?.data?.new_data?.user_data,
          entity_data: {
            ...dataHolder?.data?.new_data?.entity_data,
            ...paymentDetails,
          } as UpdateEntityRequest,
          dividend_reinvestment: dividend_reinvestment,
        });
      }
      moveForward && moveForward();
    },
    enableReinitialize: true,
  });

  const [dirty, setDirty] = useState(false);
  const setUnsavedChanges = useUnsavedChangesStore(state => state.setUnsavedChanges);
  setUnsavedChanges(dirty);

  const { mutateAsync: updateEntity, isLoading } = useUpdateInvestorEditMutation({
    setErrors: formik.setErrors,
  });

  useEffect(() => {
    const currentReinvestment: Record<number, boolean> = Object.fromEntries(
      shareholderData?.shareholders?.[0]?.dividend_reinvestment_extended?.map(item => [
        item.id,
        item.is_reinvest,
      ]) || [],
    );
    const dividend_reinvestment = !isUpdate
      ? undefined
      : dataHolder?.data?.new_data?.dividend_reinvestment || currentReinvestment;

    formik.setFieldValue('dividend_reinvestment', dividend_reinvestment);
    formik.setFieldError('dividend_reinvestment', '');
  }, [dataHolder?.data?.new_data?.dividend_reinvestment, isUpdate, shareholderData?.shareholders]);

  const handleOnSkip = async () => {
    setDirty(false);
    formik.setErrors({});
    const { dividend_reinvestment, ...values } = formik.values;
    const paymentDetails = {
      ...values,
      bank_name: values.bank_name || 'Unknown',
      bank_account_bsb: values.bank_account_bsb || 'Unknown',
      bank_account_swift: values.country !== 'AUS' ? values.bank_account_swift || 'Unknown' : '',
      bank_account_iban: isIbanCountry(values.country) ? values.bank_account_iban || 'Unknown' : '',
      bank_account_routing_number:
        values.country === 'USA' ? values.bank_account_routing_number || 'Unknown' : '',
      bank_account_name: values.bank_account_name || 'Unknown',
      bank_account_number: values.bank_account_number || 'Unknown',
    };

    await updateEntity({
      id: dataHolder?.data?.id,
      user_data: dataHolder?.data?.new_data?.user_data,
      entity_data: {
        ...dataHolder?.data?.new_data?.entity_data,
        ...paymentDetails,
      } as UpdateEntityRequest,
      dividend_reinvestment,
    });
    moveForward && moveForward();
  };

  const handleChangeReinvestment = useCallback(
    (id: number) => (reinvestment: boolean) => {
      setDirty(true);
      formik.setFieldValue('dividend_reinvestment', {
        ...(formik.values.dividend_reinvestment || {}),
        [id]: reinvestment,
      });
      formik.setFieldError('dividend_reinvestment', '');
    },
    [formik.values.dividend_reinvestment],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDirty(true);
    const field = e.target.name;
    const value = e.target.value;
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  return (
    <OnboardingHolderWrapper
      title="Payment instructions"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? handleOnSkip : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <div className="w-full gap-2 flex flex-col items-start">
          <p className="text-[16px] leading-[24px]">
            Please provide your bank account details below for all cash payments
          </p>
          <p className="text-[14px] leading-[22px] text-gray-600">
            Payments will only be made electronically to the bank account in your name. We will not
            make any payments into third party bank accounts.
          </p>
          <p className="text-[14px] leading-[22px] text-gray-600">
            Investors who do not provide EFT details will have distribution payments defaulted into
            reinvestment (where eligible).
          </p>
        </div>
        <div className="w-full flex flex-col items-start gap-8">
          <Alert
            icon={AlertIcon}
            variant="neutral-2"
            label="Distribution payments will be made by EFT to investors."
          />
          <TextInputField label="Bank name" name="bank_name" onChange={handleChange} />
          {formik.values.country === 'AUS' ? (
            <TextInputField
              label="BSB"
              name="bank_account_bsb"
              placeholder="123-456"
              onChange={handleChange}
            />
          ) : (
            <>
              {formik.values.country === 'USA' && (
                <TextInputField
                  label="Routing number"
                  name="bank_account_routing_number"
                  placeholder="021000021"
                  hintLabel="Enter the 9-digit ABA routing number for your US bank account."
                  onChange={handleChange}
                />
              )}
              {isIbanCountry(formik.values.country) && (
                <TextInputField
                  label="IBAN"
                  name="bank_account_iban"
                  placeholder="DE89370400440532013000"
                  hintLabel="Enter your International Bank Account Number without spaces."
                  onChange={handleChange}
                />
              )}
              <TextInputField
                label="SWIFT/BIC"
                name="bank_account_swift"
                placeholder="DEUTDEFF"
                hintLabel="Enter your bank's 8 or 11 character SWIFT/BIC code."
                onChange={handleChange}
              />
            </>
          )}
          <TextInputField
            label="account number"
            name="bank_account_number"
            onChange={handleChange}
          />
          <TextInputField label="account name" name="bank_account_name" onChange={handleChange} />
        </div>
        <div className="w-full flex flex-col items-start gap-4">
          {shareholderData?.shareholders?.[0]?.dividend_reinvestment_extended?.map(item => (
            <InfoCard
              key={item.id}
              title={item.register_class_name}
              classes={{ block: 'w-full', content: 'p-0 px-6 pb-6 pt-4' }}
            >
              <SelectButtons<boolean>
                label="REINVESTMENT DISTRIBUTION ELECTION"
                options={dividendReinvestmentOptions}
                selected={dividendReinvestmentOptions.find(
                  ({ value }) => value === formik.values.dividend_reinvestment?.[item.id],
                )}
                onChange={handleChangeReinvestment(item.id)}
                error={formik.errors.dividend_reinvestment?.[item.id]}
              />
            </InfoCard>
          ))}
        </div>
      </>
    </OnboardingHolderWrapper>
  );
};
